import { invokeEdgeOrPresent } from '../../shared/supabase/invokeEdgeOrPresent';
import { supabase } from '../../shared/supabase/supabaseClient';
import { captureOpsFailure } from '../../shared/sentry/sentryOps';
import { customerPortalBaseUrl } from '../../shared/customerPortalUrl';
import { mapInviteError } from './mapInviteError';

export { isActiveEmployeeEmail } from './inviteExtensionDetect';

type InviteEdgeBody = {
  ok?: boolean;
  message?: string;
  error?: string;
  code?: string;
  email?: string;
};

/** Acepta una invitación de empleado desde la app usando la sesión del usuario. */
export async function acceptEmployeeInvitation(token: string): Promise<{ ok: boolean; message: string }> {
  const fallback = 'No pudimos aceptar la invitación.';
  const { data, error, presented } = await invokeEdgeOrPresent(
    'accept-employee-invitation',
    { body: { token } },
    { module: 'team', op: 'team.invite_accept', fallbackMessage: fallback }
  );
  const body = (data ?? {}) as InviteEdgeBody;
  if (error) {
    if (presented) {
      return { ok: false, message: 'Algo falló. Reintentá en un momento.' };
    }
    return {
      ok: false,
      message: mapInviteError(
        body.error ? { code: body.code, message: body.error, email: body.email } : error.message,
        fallback
      ),
    };
  }
  if (body.error || body.ok === false) {
    return {
      ok: false,
      message: mapInviteError(
        { code: body.code, message: body.error ?? body.message, email: body.email },
        fallback
      ),
    };
  }
  return {
    ok: true,
    message: mapInviteError({ code: body.code, message: body.message }, 'Invitación completada.'),
  };
}

/** Rechaza una invitación de empleado desde la app usando la sesión del usuario. */
export async function rejectEmployeeInvitation(token: string): Promise<{ ok: boolean; message: string }> {
  const fallback = 'No pudimos rechazar la invitación.';
  const { data, error, presented } = await invokeEdgeOrPresent(
    'reject-employee-invitation',
    { body: { token } },
    { module: 'team', op: 'team.invite_reject', fallbackMessage: fallback }
  );
  const body = (data ?? {}) as InviteEdgeBody;
  if (error) {
    if (presented) {
      return { ok: false, message: 'Algo falló. Reintentá en un momento.' };
    }
    return {
      ok: false,
      message: mapInviteError(
        body.error ? { code: body.code, message: body.error, email: body.email } : error.message,
        fallback
      ),
    };
  }
  if (body.error || body.ok === false) {
    return {
      ok: false,
      message: mapInviteError(
        { code: body.code, message: body.error ?? body.message, email: body.email },
        fallback
      ),
    };
  }
  return {
    ok: true,
    message: mapInviteError({ code: body.code, message: body.message }, 'Invitación rechazada.'),
  };
}

export const portalInviteBase = () => customerPortalBaseUrl();

export function invitationUrlFromToken(token: string | null): string | null {
  const t = typeof token === 'string' ? token.trim() : '';
  if (!t) return null;
  return `${portalInviteBase()}/invitacion?token=${encodeURIComponent(t)}`;
}

export type InvitationPermissions = Record<
  string,
  {
    canView: boolean;
    canEdit: boolean;
    canVoid: boolean;
    canDiscount: boolean;
    canManageAgenda?: boolean;
    canViewOthers?: boolean;
    actions?: Record<string, boolean>;
  }
>;

export type InviteEmployeePayload = {
  email: string;
  fullName?: string;
  role?: string;
  /** Permisos por módulo que se aplicarán al aceptar. */
  permissions?: InvitationPermissions;
  /** Compat: una sucursal */
  branchId?: string;
  /** Varias sucursales (preferido; si se envía, debe incluir al menos una) */
  branchIds?: string[];
};

/** Respuesta de la Edge Function (snake_case). */
export type InvitationResponseRaw = {
  id: string;
  email: string;
  status: string;
  token: string | null;
  branch_id: string | null;
  full_name: string | null;
  role: string | null;
  permissions: InvitationPermissions | null;
  invited_at: string;
  /** True si el Edge detectó re-invite a empleado ya aceptado (suma sucursales). */
  is_extension?: boolean;
};

export const inviteEmployee = async (payload: InviteEmployeePayload): Promise<InvitationResponseRaw> => {
  const fallback = 'No pudimos enviar la invitación.';
  const { data, error, presented, kind } = await invokeEdgeOrPresent(
    'employee-invitations',
    { body: payload },
    { module: 'team', op: 'team.invite', fallbackMessage: fallback }
  );
  const body = (data ?? {}) as {
    invitation?: InvitationResponseRaw;
    error?: string;
    code?: string;
    email?: string;
    is_extension?: boolean;
  };
  if (error) {
    const fail = new Error(
      mapInviteError(
        body.error ? { code: body.code, message: body.error, email: body.email } : error.message,
        fallback
      )
    );
    if (!presented && kind === 'unexpected') {
      captureOpsFailure({
        module: 'team',
        op: 'team.invite',
        error: fail,
        ingestError: error.message ?? fail.message,
      });
    }
    throw fail;
  }
  if (body?.error) {
    throw new Error(mapInviteError({ code: body.code, message: body.error }, fallback));
  }
  if (!body?.invitation || typeof body.invitation !== 'object') {
    const fail = new Error('Respuesta inválida del servidor.');
    captureOpsFailure({
      module: 'team',
      op: 'team.invite',
      error: fail,
      ingestError: fail.message,
    });
    throw fail;
  }
  const inv = body.invitation;
  const isExtension = body.is_extension === true || inv.is_extension === true;
  return {
    id: inv.id,
    email: inv.email,
    status: inv.status,
    token: inv.token ?? null,
    branch_id: inv.branch_id ?? null,
    full_name: inv.full_name ?? null,
    role: inv.role ?? null,
    permissions: inv.permissions ?? null,
    invited_at: typeof inv.invited_at === 'string' ? inv.invited_at : new Date().toISOString(),
    is_extension: isExtension,
  };
};

export type InvitationListItem = {
  id: string;
  email: string;
  full_name: string | null;
  role: string | null;
  status: string;
  invited_at: string;
  branch_id: string | null;
  branch_ids: string[];
  expires_at: string | null;
  /** Presente para armar el link del portal; solo lectura del dueño (RLS). */
  token: string | null;
  permissions: InvitationPermissions | null;
};

export async function fetchMyInvitations(): Promise<InvitationListItem[]> {
  const {
    data: { session },
  } = await supabase.auth.getSession();
  if (!session) return [];
  const { data, error } = await supabase
    .from('employee_invitations')
    .select(
      'id, email, full_name, role, status, invited_at, branch_id, expires_at, token, permissions, employee_invitation_branches ( branch_id )'
    )
    .order('invited_at', { ascending: false });
  if (error) {
    throw new Error(mapInviteError(error.message, 'No se pudieron cargar las invitaciones.'));
  }
  return (data ?? []).map((row: any) => {
    const bridge = row.employee_invitation_branches as { branch_id: string }[] | undefined;
    const fromBridge = Array.isArray(bridge) ? bridge.map((b) => b.branch_id) : [];
    const branch_ids =
      fromBridge.length > 0 ? fromBridge : row.branch_id ? [row.branch_id as string] : [];
    return {
      id: row.id,
      email: row.email,
      full_name: row.full_name ?? null,
      role: row.role ?? null,
      status: row.status ?? 'pending',
      invited_at: row.invited_at,
      branch_id: row.branch_id ?? null,
      branch_ids,
      expires_at: row.expires_at ?? null,
      token: typeof row.token === 'string' ? row.token : null,
      permissions: (row.permissions as InvitationPermissions | null) ?? null,
    };
  });
}

export async function cancelEmployeeInvitation(invitationId: string): Promise<void> {
  const fallback = 'No pudimos cancelar la invitación.';
  const { data, error, presented } = await invokeEdgeOrPresent(
    'employee-invitations',
    { body: { action: 'cancel', invitationId } },
    { module: 'team', op: 'team.invite_cancel', fallbackMessage: fallback }
  );
  if (error) {
    const fail = new Error(mapInviteError(error.message, fallback));
    if (!presented) {
      captureOpsFailure({
        module: 'team',
        op: 'team.invite_cancel',
        error: fail,
        ingestError: error.message ?? fail.message,
        itemId: invitationId,
      });
    }
    throw fail;
  }
  const body = (data ?? {}) as { error?: string; code?: string };
  if (body.error) throw new Error(mapInviteError({ code: body.code, message: body.error }, fallback));
}

export async function resendEmployeeInvitation(invitationId: string): Promise<InvitationResponseRaw | null> {
  const fallback = 'No pudimos reenviar la invitación.';
  const { data, error, presented } = await invokeEdgeOrPresent(
    'employee-invitations',
    { body: { action: 'resend', invitationId } },
    { module: 'team', op: 'team.invite_resend', fallbackMessage: fallback }
  );
  if (error) {
    const fail = new Error(mapInviteError(error.message, fallback));
    if (!presented) {
      captureOpsFailure({
        module: 'team',
        op: 'team.invite_resend',
        error: fail,
        ingestError: error.message ?? fail.message,
        itemId: invitationId,
      });
    }
    throw fail;
  }
  const body = (data ?? {}) as { invitation?: InvitationResponseRaw; error?: string; code?: string };
  if (body.error) throw new Error(mapInviteError({ code: body.code, message: body.error }, fallback));
  return body.invitation ?? null;
}

/** Copy relativa de vencimiento para filas de invitación. */
export function invitationExpiryRelative(expiresAt: string | null | undefined, now = new Date()): string | null {
  if (!expiresAt) return null;
  const exp = new Date(expiresAt);
  if (Number.isNaN(exp.getTime())) return null;
  const ms = exp.getTime() - now.getTime();
  if (ms <= 0) return 'Venció';
  const days = Math.ceil(ms / (24 * 60 * 60 * 1000));
  if (days === 1) return 'Vence mañana';
  if (days <= 14) return `Vence en ${days} días`;
  return `Vence el ${exp.toLocaleDateString('es-AR')}`;
}
